import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons/faEllipsisVertical';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlass';
import { faComment } from '@fortawesome/pro-solid-svg-icons/faComment';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { faPaperclip } from '@fortawesome/pro-solid-svg-icons/faPaperclip';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { faStar as faStarRegular } from '@fortawesome/pro-regular-svg-icons/faStar';

import { FamilyPaywallModal } from 'us-react-components/dist/FamilyPaywallModal/FamilyPaywallModal';
import {
  SittersSearchFilter,
  SittersSearchFilterValue,
} from 'us-react-components/dist/SittersSearchFilter/SittersSearchFilter';
import {
  FavoriteSitterService,
  getApiErrorMessage,
  InterestedSitter,
  InterestedSitterService,
  Job,
  JobAvailabilityType,
  JobHiddenSitterService,
  JobService,
  JobType,
  Sitter,
} from 'us-web-services';
import {
  DateFormat,
  formatDateFromString,
} from 'us-react-components/dist/utils/date';

import { Button, ButtonSize, ButtonType } from '../../components';
import { Storage } from '../../constants';
import { useDependencies } from '../../dependencies';
import { ModalName } from '../../modals/constants';
import { useModal } from '../../modals/modalContext';
import { checkForPaywall } from '../../utils/PaywallUtil';
import { getJobHeading } from '../../utils/JobHeading';
import { DisplayUtil } from '../../utils/DisplayUtil';
import { BackgroundCheckRequest } from '../Sitters/components/BackgroundCheckRequest';

type CandidateType = 'all' | 'passed';

// Mirrors Angular interested-sitter-card.js:76 — `hasResponse` flips the
// paperclip icon on, signaling that the caregiver attached an intro
// (free-form message or screening-question answers) with their interest.
const hasIntro = (interest: InterestedSitter): boolean =>
  !!interest.message || (Array.isArray(interest.answers) && interest.answers.length > 0);

// Mirrors Angular rating-stars directive — five stars rendered with fractional
// fill rounded to nearest half. Sitter `rating` is a 0-5 float.
const RatingStars = ({ rating, count }: { rating: number; count: number }): JSX.Element => {
  const rounded = Math.round(rating * 2) / 2;
  return (
    <div className='flex items-center gap-1 text-amber-500 text-sm'>
      {[1, 2, 3, 4, 5].map((i) => (
        <FontAwesomeIcon key={i} icon={i <= rounded ? faStar : faStarRegular} />
      ))}
      <span className='text-neutral-700 ml-1'>({count})</span>
    </div>
  );
};

const formatSlashDate = (iso?: string): string =>
  iso ? formatDateFromString(iso, DateFormat.DATE_LONG_FORMAT_SLASHES) : '';

export const JobInterest = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { tracker, config } } = useDependencies();
  const modal = useModal();
  const openIdentity = (): void => modal?.setModalName(ModalName.IdentityVerification);
  const user = stores.user.user;

  const PAGE_SIZE = 10;
  const [job, setJob] = useState<Job | null>(null);
  const [applicants, setApplicants] = useState<InterestedSitter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<SittersSearchFilterValue>({});
  const [candidateType, setCandidateType] = useState<CandidateType>('all');
  const [page, setPage] = useState(1);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallSitter, setPaywallSitter] = useState<Sitter | null>(null);
  const [paywallSitterId, setPaywallSitterId] = useState<number | null>(null);

  const sitterMatchesFilter = (sitter: Sitter): boolean => {
    const hasAll = (ids: number[] | undefined, have: Array<{ id?: number }> | undefined): boolean => {
      if (!ids || ids.length === 0) return true;
      const haveIds = new Set((have ?? []).map((x) => x.id).filter((x): x is number => x != null));
      return ids.every((id) => haveIds.has(id));
    };
    if (!hasAll(filter.certificationIds, sitter.certifications)) return false;
    if (!hasAll(filter.languageIds, sitter.languages)) return false;
    if (!hasAll(filter.teachingSkillIds, sitter.teachingSkills)) return false;
    if (!hasAll(filter.specialSituationIds, sitter.specialSituations)) return false;
    if (!hasAll(filter.specialExperienceIds, sitter.specialExperiences)) return false;
    if (!hasAll(filter.specialServiceIds, sitter.specialServices)) return false;
    if (filter.acceptsCreditCard && !sitter.acceptsCreditCards) return false;
    const minRate = sitter.rates?.[0]?.minimumRate;
    if (filter.maxRate != null && minRate != null && minRate > filter.maxRate) return false;
    if (filter.name) {
      const target = `${sitter.firstName ?? ''} ${sitter.lastName ?? ''}`.toLowerCase();
      if (!target.includes(filter.name.toLowerCase())) return false;
    }
    return true;
  };

  const isPassed = (interest: InterestedSitter): boolean => !!interest.hidden;

  const tabFiltered = useMemo(
    () => applicants.filter((i) => (candidateType === 'passed' ? isPassed(i) : !isPassed(i))),
    [applicants, candidateType],
  );

  const visibleApplicants = tabFiltered.filter((interest) => {
    const sitter = interest.sitter as Sitter | undefined;
    return !!sitter && sitterMatchesFilter(sitter);
  });
  const totalPages = Math.max(1, Math.ceil(visibleApplicants.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedApplicants = visibleApplicants.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const passedCount = applicants.filter(isPassed).length;
  const activeCount = applicants.length - passedCount;

  // Reset to page 1 whenever the client-side filter narrows the list or the
  // user switches between All / Passed.
  useEffect(() => {
    setPage(1);
  }, [filter, candidateType]);

  useEffect(() => {
    document.title = 'UrbanSitter - Interested Caregivers';
  }, []);

  useEffect(() => {
    const jobId = Number(pathId);
    if (!jobId || !user?.id) return;
    let cancelled = false;

    const fetchAllApplicants = async (): Promise<InterestedSitter[]> => {
      // Omitting `passed` returns BOTH hidden and non-hidden interests — we
      // split client-side into All / Passed tabs. The API caps each page at
      // its request limit, so follow meta.size across pages to fetch everyone.
      const limit = 100;
      const all: InterestedSitter[] = [];
      let pageNumber = 1;
      let total = 0;
      do {
        const resp = await InterestedSitterService.getByFilter({
          params: { jobId, userId: user.id, parentId: user.id, withdrawn: false, page: pageNumber, limit },
        });
        const rows = (resp.data?.data ?? []) as InterestedSitter[];
        all.push(...rows);
        total = resp.data?.meta?.size ?? all.length;
        pageNumber += 1;
        if (rows.length === 0) break;
      } while (all.length < total);
      return all;
    };

    const loadAll = async (): Promise<void> => {
      try {
        const [jobResp, allApplicants] = await Promise.all([
          JobService.findById(jobId, { params: { userId: user.id } }),
          fetchAllApplicants(),
        ]);
        if (cancelled) return;
        setJob(jobResp.data?.data ?? null);
        setApplicants(allApplicants);
      } catch (err) {
        console.warn('[JobInterest] load failed', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadAll();
    return (): void => {
      cancelled = true;
    };
  }, [pathId, user?.id]);

  const goToApplicant = (sitterId?: number): void => {
    if (sitterId == null) return;
    navigate(`/job/${pathId}/applicant/${sitterId}`);
  };

  const goToSearch = (): void => {
    const booking = job?.booking;
    const params = new URLSearchParams();
    if (booking?.rate != null) params.set('maximumRate', String(booking.rate));
    if (booking?.numberOfChildren != null) {
      params.set('numberOfChildren', String(booking.numberOfChildren));
    }
    if (booking?.address?.city) params.set('city', booking.address.city);
    if (booking?.address?.state) params.set('state', booking.address.state);
    if (booking?.typeId != null) params.set('verticalTypeId', String(booking.typeId));
    if (job?.type === JobType.ONE_TIME && booking) {
      if (booking.start) params.set('start', booking.start);
      if (booking.end) params.set('end', booking.end);
    } else {
      params.set('availabilityType', 'recurring');
      params.set(
        'searchTypeCode',
        job?.availability === JobAvailabilityType.fullTime ? 'FULL_TIME' : 'PART_TIME',
      );
    }
    navigate(`/sitters?${params.toString()}`);
  };

  // Angular interested-sitter-card.js:113 — book CTA navigates to the wizard
  // start (/parent/bookings/select), not the inner select-date step. The
  // wizard picks up sitterId + jobId from the query string.
  const bookSitter = (sitterId?: number): void => {
    if (!sitterId || !pathId) return;
    tracker.track('clicked-button', {
      action: 'Book CTA',
      component: 'Interested Caregivers',
    });
    stores.verification.checkVerification(() => {
      const cookies = new Cookies();
      cookies.set(
        Storage.BOOKING_ORIGIN,
        `/app${window.location.pathname}${window.location.search}`,
        { path: '/' },
      );
      navigate(`/parent/bookings/select?sitterId=${sitterId}&jobId=${pathId}`);
    }, openIdentity);
  };

  const interviewSitter = (sitterId?: number): void => {
    if (!sitterId) return;
    tracker.track('clicked-button', {
      action: 'Interview CTA',
      component: 'Interested Caregivers',
    });
    stores.verification.checkVerification(() => {
      navigate(`/parent/interviews/select?sitterId=${sitterId}`);
    }, openIdentity);
  };

  const messageSitter = (sitterId?: number, sitter?: Sitter): void => {
    if (!sitterId || !user?.id) return;
    tracker.track('clicked-button', {
      action: 'Message CTA',
      component: 'Interested Caregivers',
    });
    stores.verification.checkVerification(() => {
      checkForPaywall({
        userId: user.id,
        sitter: sitter ?? ({} as Sitter),
        action: (): void =>
          navigate(`/parent/${user.id}/messages/${sitterId}`),
        openPaywall: (): void => {
          setPaywallSitter(sitter ?? null);
          setPaywallSitterId(sitterId);
          setPaywallOpen(true);
        },
      });
    }, openIdentity);
  };

  const toggleHide = async (interest: InterestedSitter): Promise<void> => {
    const sitterId = interest.sitter?.id;
    if (!sitterId) return;
    const jobId = interest.job?.id ?? job?.id;
    if (!jobId) return;
    const next = !isPassed(interest);
    try {
      if (next) {
        const resp = await JobHiddenSitterService.hide(jobId, sitterId);
        const created = (resp.data?.data ?? {}) as { id?: number };
        setApplicants((prev) =>
          prev.map((applicant) =>
            applicant.sitter?.id === sitterId
              ? { ...applicant, hidden: true, hiddenSitterId: created.id }
              : applicant,
          ),
        );
      } else if (interest.hiddenSitterId) {
        await JobHiddenSitterService.unhide(interest.hiddenSitterId);
        setApplicants((prev) =>
          prev.map((applicant) =>
            applicant.sitter?.id === sitterId
              ? { ...applicant, hidden: false, hiddenSitterId: undefined }
              : applicant,
          ),
        );
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? t('pages.jobInterest.couldntUpdate'));
    }
  };

  const toggleFavorite = async (sitter: Sitter): Promise<void> => {
    if (!sitter.id || !user?.id) return;
    const rel = sitter.relationship as
      | { favorite?: { id?: number }; bookmarked?: boolean; bookmarkedId?: number }
      | undefined;
    const favoriteId = rel?.favorite?.id ?? rel?.bookmarkedId;
    try {
      if (favoriteId) {
        await FavoriteSitterService.delete(favoriteId);
      } else {
        await FavoriteSitterService.create({
          sitterId: sitter.id,
          parentId: user.id,
        } as never);
      }
      setApplicants((prev) =>
        prev.map((applicant) => {
          if ((applicant.sitter as Sitter | undefined)?.id !== sitter.id) return applicant;
          const existingRel = (applicant.sitter?.relationship ?? {}) as Record<string, unknown>;
          const nextRel = favoriteId
            ? { ...existingRel, favorite: undefined, bookmarked: false, bookmarkedId: undefined }
            : { ...existingRel, favorite: { id: -1 }, bookmarked: true };
          return {
            ...applicant,
            sitter: { ...(applicant.sitter as Sitter), relationship: nextRel as Sitter['relationship'] },
          };
        }),
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? t('pages.jobInterest.couldntUpdateFavorite'));
    }
  };

  if (isLoading) {
    return (
      <div className='container py-8'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('pages.jobApplicant.loading')}</span>
      </div>
    );
  }

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-1'>{t('pages.jobApplicant.heading')}</h1>
      {job && (
        <p className='mb-6'>
          <Link id='back-to-job' to={`/job/${job.id}`} className='underline text-sapphire'>
            {getJobHeading(job, t)}
          </Link>
        </p>
      )}

      {applicants.length === 0 ? (
        <div className='us-card rounded p-6 text-center'>
          <FontAwesomeIcon icon={faMagnifyingGlass} className='text-4xl text-neutral-400 mb-4' />
          <h1 className='font-light text-2xl mb-4'>{t('pages.jobApplicant.nullSearchHeading')}</h1>
          <Button
            id='null-search'
            buttonType={ButtonType.primary}
            buttonSize={ButtonSize.wide}
            onClick={goToSearch}
            className='my-2'
          >
            {t('pages.jobApplicant.nullSearchCta')}
          </Button>
        </div>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-12 gap-4'>
          <aside className='md:col-span-3'>
            <SittersSearchFilter
              value={filter}
              onChange={setFilter}
              isJobInterest
            />
          </aside>
          <div className='md:col-span-9 space-y-4'>
            {/* Candidate-type tabs (All / Passed) — mirrors
                apps/angular/src/js/ng/parent/sitters-management. */}
            <div
              role='tablist'
              aria-label='Applicant filter'
              className='flex border-b border-neutral-200'
            >
              {([
                { code: 'all', label: 'All', count: activeCount },
                { code: 'passed', label: 'Passed', count: passedCount },
              ] as Array<{ code: CandidateType; label: string; count: number }>).map((tab) => {
                const active = candidateType === tab.code;
                return (
                  <button
                    key={tab.code}
                    type='button'
                    role='tab'
                    aria-selected={active}
                    onClick={(): void => setCandidateType(tab.code)}
                    className={`px-4 py-2 font-semibold text-base transition border-b-2 ${
                      active
                        ? 'border-sapphire text-sapphire'
                        : 'border-transparent text-neutral-600 hover:text-neutral-900'
                    }`}
                  >
                    {tab.label} <span className='font-normal'>({tab.count})</span>
                  </button>
                );
              })}
            </div>

            {visibleApplicants.length === 0 && (
              <div className='us-card rounded p-4'>
                <h4 className='mb-1 font-semibold'>
                  {t('pages.jobInterest.noMatchHeading')}
                </h4>
                <p>{t('pages.jobInterest.noMatchBody')}</p>
              </div>
            )}

            {pagedApplicants.map((interest) => {
              const sitter = interest.sitter as Sitter | undefined;
              if (!sitter) return null;
              return (
                <InterestedApplicantRow
                  key={sitter.id}
                  interest={interest}
                  sitter={sitter}
                  jobHasRate={!!job?.booking?.rate}
                  onBook={(): void => bookSitter(sitter.id)}
                  onInterview={(): void => interviewSitter(sitter.id)}
                  onMessage={(): void => messageSitter(sitter.id, sitter)}
                  onViewApplicant={(): void => goToApplicant(sitter.id)}
                  onViewProfile={(): void => navigate(`/sitter/${sitter.id}`)}
                  onTogglePass={(): void => {
                    toggleHide(interest);
                  }}
                  onToggleFavorite={(): void => {
                    toggleFavorite(sitter);
                  }}
                />
              );
            })}

            {totalPages > 1 && (
              <div className='flex items-center justify-center gap-3 pt-4'>
                <Button
                  buttonType={ButtonType.secondary}
                  disabled={currentPage <= 1}
                  onClick={(): void => setPage(currentPage - 1)}
                >
                  {t('common.previous')}
                </Button>
                <span className='text-sm'>
                  {t('pages.jobInterest.pageOf', { current: currentPage, total: totalPages })}
                </span>
                <Button
                  buttonType={ButtonType.secondary}
                  disabled={currentPage >= totalPages}
                  onClick={(): void => setPage(currentPage + 1)}
                >
                  {t('common.next')}
                </Button>
              </div>
            )}

            {job?.type !== 'one_time' && <BackgroundCheckRequest isBanner />}
          </div>
        </div>
      )}

      {user?.id && (
        <FamilyPaywallModal
          showModal={paywallOpen}
          onClose={(): void => setPaywallOpen(false)}
          onSuccess={(): void => {
            setPaywallOpen(false);
            if (paywallSitterId) {
              navigate(`/parent/${user.id}/messages/${paywallSitterId}`);
            }
          }}
          sitter={paywallSitter ?? undefined}
          userId={user.id}
          stripeKey={config.STRIPE_KEY}
          recaptchaKey={config.RECAPTCHA_KEY}
        />
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Interested-applicant row — replaces the SitterCard. Matches the layout of
// apps/angular/src/js/ng/parent/job/interested-sitter-card with avatar,
// summary text, 3-dot overflow menu, and desktop CTA column.
// ---------------------------------------------------------------------------

interface RowProps {
  interest: InterestedSitter;
  sitter: Sitter;
  jobHasRate: boolean;
  onBook: () => void;
  onInterview: () => void;
  onMessage: () => void;
  onViewApplicant: () => void;
  onViewProfile: () => void;
  onTogglePass: () => void;
  onToggleFavorite: () => void;
}

const InterestedApplicantRow = ({
  interest,
  sitter,
  jobHasRate,
  onBook,
  onInterview,
  onMessage,
  onViewApplicant,
  onViewProfile,
  onTogglePass,
  onToggleFavorite,
}: RowProps): JSX.Element => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const passed = !!interest.hidden;
  const lastMessaged = (sitter.relationship as { lastMessaged?: string } | undefined)?.lastMessaged;
  const rel = sitter.relationship as
    | { favorite?: { id?: number }; bookmarked?: boolean; blocked?: boolean }
    | undefined;
  const favorited = !!(rel?.favorite?.id || rel?.bookmarked);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return (): void => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const distance = sitter.relationship?.distance;

  return (
    <div
      id={`interested-sitter-card-${sitter.id}`}
      className='us-card rounded p-4 border border-neutral-200 shadow-sm bg-white grid grid-cols-1 md:grid-cols-12 gap-3'
    >
      {/* Avatar + name + summary */}
      <div className='md:col-span-9 flex gap-4 items-start'>
        <div className='relative flex-shrink-0'>
          <button
            type='button'
            onClick={onViewApplicant}
            aria-label={t('pages.jobInterest.viewApplicationAriaLabel', { name: sitter.firstName ?? '' })}
            className='block'
          >
            <img
              alt={sitter.firstName ?? ''}
              src={sitter.pictureUrl}
              className='w-20 h-20 rounded-full object-cover'
            />
          </button>
          <button
            type='button'
            onClick={onToggleFavorite}
            aria-label={favorited
              ? t('pages.jobInterest.removeFavoriteAriaLabel')
              : t('pages.jobInterest.saveFavoriteAriaLabel')}
            className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white shadow flex items-center justify-center text-sm ${
              favorited ? 'text-bubblegum' : 'text-neutral-400'
            }`}
          >
            <FontAwesomeIcon icon={favorited ? faHeart : faHeartRegular} />
          </button>
        </div>

        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 flex-wrap'>
            <button
              type='button'
              onClick={onViewApplicant}
              className='font-semibold text-base text-left hover:underline'
            >
              {sitter.firstName}
            </button>
            {hasIntro(interest) && (
              <FontAwesomeIcon
                icon={faPaperclip}
                className='text-neutral-500 text-sm'
                title={t('pages.jobInterest.introAttached')}
                aria-label={t('pages.jobInterest.introAttached')}
              />
            )}
          </div>
          {!!sitter.repeatFamilies && (
            <div className='text-sm text-neutral-700'>
              {t('pages.jobInterest.repeatFamilies', { count: sitter.repeatFamilies })}
            </div>
          )}
          {!jobHasRate && sitter.displayRate?.minimumRate != null && (
            <div className='text-sm text-neutral-700'>
              {t('pages.jobInterest.ratePerHour', { rate: sitter.displayRate.minimumRate })}
            </div>
          )}
          {sitter.reviewCount != null && sitter.reviewCount > 0 && sitter.rating != null && (
            <RatingStars rating={sitter.rating} count={sitter.reviewCount} />
          )}
          {!!distance && (
            <div className='text-sm text-neutral-700'>
              {DisplayUtil.getDistanceText(t, distance)}
            </div>
          )}
          {lastMessaged && (
            <div className='text-sm text-neutral-700 mt-1'>
              <FontAwesomeIcon icon={faComment} className='mr-1 text-neutral-500' />
              {t('pages.jobInterest.lastMessage', { date: formatSlashDate(lastMessaged) })}
            </div>
          )}
        </div>
      </div>

      {/* Actions column: three-dot menu on top, CTAs below for desktop */}
      <div className='md:col-span-3 flex flex-col gap-2'>
        <div className='flex justify-end' ref={menuRef}>
          <div className='relative'>
            <Button
              buttonType={ButtonType.tertiary}
              aria-label={t('pages.jobInterest.moreOptionsAriaLabel')}
              aria-haspopup='menu'
              aria-expanded={menuOpen}
              onClick={(): void => setMenuOpen((o) => !o)}
              className='text-neutral-600 px-2 py-1'
            >
              <FontAwesomeIcon icon={faEllipsisVertical} />
            </Button>
            {menuOpen && (
              <ul
                role='menu'
                className='absolute right-0 mt-1 bg-white border border-neutral-200 rounded shadow-lg z-20 min-w-[14rem] py-1 text-sm'
              >
                <li role='menuitem'>
                  <button
                    type='button'
                    className='w-full text-left px-4 py-2 hover:bg-neutral-100'
                    onClick={(): void => { setMenuOpen(false); onViewApplicant(); }}
                  >
                    {t('pages.jobInterest.viewApplicantSummary')}
                  </button>
                </li>
                <li role='menuitem'>
                  <button
                    type='button'
                    className='w-full text-left px-4 py-2 hover:bg-neutral-100'
                    onClick={(): void => { setMenuOpen(false); onViewProfile(); }}
                  >
                    {t('pages.jobInterest.viewFullProfile')}
                  </button>
                </li>
                <li role='menuitem'>
                  <button
                    type='button'
                    className='w-full text-left px-4 py-2 hover:bg-neutral-100'
                    onClick={(): void => { setMenuOpen(false); onBook(); }}
                  >
                    {t('pages.jobInterest.bookCta')}
                  </button>
                </li>
                <li role='menuitem'>
                  <button
                    type='button'
                    className='w-full text-left px-4 py-2 hover:bg-neutral-100'
                    onClick={(): void => { setMenuOpen(false); onInterview(); }}
                  >
                    {t('pages.jobInterest.interviewCta')}
                  </button>
                </li>
                <li role='menuitem'>
                  <button
                    type='button'
                    className='w-full text-left px-4 py-2 hover:bg-neutral-100'
                    onClick={(): void => { setMenuOpen(false); onMessage(); }}
                  >
                    {t('pages.jobInterest.messageCta')}
                  </button>
                </li>
                {!passed && (
                  <li role='menuitem'>
                    <button
                      type='button'
                      className='w-full text-left px-4 py-2 hover:bg-neutral-100 text-red-600'
                      onClick={(): void => { setMenuOpen(false); onTogglePass(); }}
                    >
                      {t('pages.jobInterest.passForJobCta')}
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>

        <Button
          id={`book-cta-${sitter.id}`}
          buttonType={ButtonType.primary}
          className='w-full'
          onClick={onBook}
        >
          {t('pages.jobInterest.bookCta')}
        </Button>
        <Button
          id={`message-cta-${sitter.id}`}
          buttonType={ButtonType.secondary}
          className='w-full'
          onClick={onMessage}
        >
          <FontAwesomeIcon icon={faComment} className='mr-1' />
          {t('pages.jobInterest.messageCta')}
        </Button>
        {!passed ? (
          <Button
            id={`pass-cta-${sitter.id}`}
            buttonType={ButtonType.secondary}
            className='w-full'
            onClick={onTogglePass}
          >
            {t('pages.jobInterest.passCta')}
          </Button>
        ) : (
          <div className='text-sm text-center'>
            {t('pages.jobInterest.passedLabel')}{' '}
            <Button
              id={`unpass-${sitter.id}`}
              buttonType={ButtonType.tertiary}
              className='underline'
              onClick={onTogglePass}
            >
              {t('pages.jobInterest.undoCta')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
